import { useMemo } from "react"

import { useEvent } from "shared/lib/useEvent"
import {
  CreateFollowUpConfigurationInput,
  UpdateFollowUpConfigurationInput
} from "shared/types/__generated__/graphql"

import { useDimConfigurationsQuery } from "entities/dim-configuration/graphql/queries.generated"
import { useCreateFollowUpConfiguration } from "entities/follow-up-configuration/api/useCreateFollowUpConfiguration"
import { useDeleteFollowUpConfiguration } from "entities/follow-up-configuration/api/useDeleteFollowUpConfiguration"
import { useFollowUpConfigurations } from "entities/follow-up-configuration/api/useFollowUpConfigurations"
import { useUpdateFollowUpConfiguration } from "entities/follow-up-configuration/api/useUpdateFollowUpConfiguration"

import { useFollowUpConfigurationProductOptions } from "./useFollowUpConfigurationProductOptions"

export function useFollowUpConfigurationAdminPageModel() {
  const { followUpConfigurations, isLoading, error } =
    useFollowUpConfigurations()
  const { data: dimConfigurationsData, loading: isLoadingProducts } =
    useDimConfigurationsQuery()

  const { createFollowUpConfiguration, loading: isCreating } =
    useCreateFollowUpConfiguration()
  const { updateFollowUpConfiguration, loading: isUpdating } =
    useUpdateFollowUpConfiguration()
  const { deleteFollowUpConfiguration, loading: isDeleting } =
    useDeleteFollowUpConfiguration()

  const products = useMemo(() => {
    const configurations = dimConfigurationsData?.dimConfigurations ?? []
    return [...configurations]
      .filter(configuration => configuration.isActive !== false)
      .sort((a, b) => a.productName.localeCompare(b.productName))
  }, [dimConfigurationsData?.dimConfigurations])
  const productOptions = useFollowUpConfigurationProductOptions(products)

  const createConfiguration = useEvent(
    async (input: CreateFollowUpConfigurationInput) => {
      await createFollowUpConfiguration(input)
    }
  )

  const updateConfiguration = useEvent(
    async (id: string, input: UpdateFollowUpConfigurationInput) => {
      await updateFollowUpConfiguration(id, input)
    }
  )

  const deleteConfiguration = useEvent(async (id: string) => {
    await deleteFollowUpConfiguration(id)
  })

  return {
    followUpConfigurations,
    productOptions,
    isLoading: isLoading || isLoadingProducts,
    error,
    isCreating,
    isUpdating,
    isDeleting,
    createConfiguration,
    updateConfiguration,
    deleteConfiguration
  }
}
